import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Title,
  CloseButton,
  ChartContainer
} from './styles';
import { toLocalDate } from '../../../utils/dateOnly';

const ProjectHistoryModal = ({ isOpen, onClose, projectHistory, employeeName, onSubmit }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !projectHistory || !chartRef.current) return;

    // Clear previous chart
    d3.select(chartRef.current).selectAll('*').remove();

    // Get sorted projects for the y-axis
    const sortedProjects = [...(projectHistory.projects || [])].sort((a, b) => a.name.localeCompare(b.name));
    if (sortedProjects.length === 0) {
      console.error('No projects available in the response');
      return;
    }
    const projectLabels = sortedProjects.map(p => p.name);

    // Generate colors for each project
    const projectColors = {};
    const colorScale = d3.scaleOrdinal(d3.schemeCategory10);
    sortedProjects.forEach((project, index) => {
      projectColors[project.name] = colorScale(index);
    });

    // Transform history data for the chart
    const historyData = (projectHistory.history || []).map(item => ({
      project: item.project.name,
      startDate: item.startDate,
      endDate: item.endDate || new Date().toISOString(),
      isOngoing: !item.endDate,
      updatedBy: item.updatedBy
    }));

    // Sort history by date
    const sortedHistory = [...historyData].sort((a, b) => 
      new Date(a.startDate) - new Date(b.startDate)
    );

    // Set up dimensions
    const margin = { top: 40, right: 30, bottom: 60, left: 250 };
    const width = chartRef.current.clientWidth - margin.left - margin.right;
    const height = chartRef.current.clientHeight - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Calculate the offset based on the y-axis labels width
    const yAxisLabelWidth = 230;
    const labelPadding = 10;
    const chartStartX = yAxisLabelWidth + labelPadding;

    // Create scales
    const xScale = d3.scaleTime()
      .domain(d3.extent([
        ...sortedHistory.map(d => new Date(d.startDate)),
        ...sortedHistory.map(d => new Date(d.endDate))
      ]))
      .range([0, width - 20])
      .nice();

    const yScale = d3.scalePoint()
      .domain(projectLabels)
      .range([height, 0])
      .padding(0.5);

    // Add axes
    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale)
        .ticks(7)
        .tickFormat(d3.timeFormat('%b %d, %Y')))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)');

    // Add y-axis with colored labels
    const yAxis = svg.append('g')
      .call(d3.axisLeft(yScale));

    // Remove the default axis line
    yAxis.select('.domain').remove();
    // Remove the default tick lines
    yAxis.selectAll('.tick line').remove();

    // Style the y-axis labels
    yAxis.selectAll('.tick')
      .append('rect')
      .attr('x', -230)
      .attr('y', -15)
      .attr('width', 220)
      .attr('height', 30)
      .attr('rx', 4)
      .style('fill', d => `${projectColors[d]}10`)
      .style('stroke', d => projectColors[d])
      .style('stroke-width', 1);

    // Add dots before text
    yAxis.selectAll('.tick')
      .append('circle')
      .attr('cx', -200)
      .attr('cy', 0)
      .attr('r', 4)
      .style('fill', d => projectColors[d]);

    // Style the text labels
    yAxis.selectAll('.tick text')
      .attr('x', -185)
      .attr('dy', '.32em')
      .style('font-size', '12px')
      .style('fill', '#333')
      .style('font-weight', '500')
      .style('text-anchor', 'start');

    // Add grid lines
    svg.selectAll('line.horizontal-grid')
      .data(projectLabels)
      .enter()
      .append('line')
      .attr('class', 'horizontal-grid')
      .attr('x1', 0)
      .attr('x2', width - 20)
      .attr('y1', d => yScale(d))
      .attr('y2', d => yScale(d))
      .style('stroke', d => `${projectColors[d]}30`)
      .style('stroke-width', 1);

    // Add project timeline bars
    svg.selectAll('.project-bar')
      .data(sortedHistory)
      .enter()
      .append('rect')
      .attr('class', 'project-bar')
      .attr('x', d => xScale(new Date(d.startDate)))
      .attr('y', d => yScale(d.project) - 10)
      .attr('width', d => xScale(new Date(d.endDate)) - xScale(new Date(d.startDate)))
      .attr('height', 20)
      .attr('rx', 4)
      .style('fill', d => `${projectColors[d.project]}50`)
      .style('stroke', d => projectColors[d.project])
      .style('stroke-width', 1)
      .style('cursor', 'pointer');

    // Add tooltips
    const tooltip = d3.select(chartRef.current)
      .append('div')
      .attr('class', 'tooltip')
      .style('position', 'absolute')
      .style('background-color', 'white')
      .style('padding', '8px')
      .style('border-radius', '4px')
      .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('z-index', 1000);

    // Add hover interactions
    svg.selectAll('.project-bar')
      .on('mouseover', (event, d) => {
        tooltip.transition()
          .duration(200)
          .style('opacity', .9);
        tooltip.html(
          `<div style="color: ${projectColors[d.project]}">
            <strong>${d.project}</strong>
           </div>
           ${toLocalDate(d.startDate)?.toLocaleDateString()} - ${d.isOngoing ? '<strong style="color: #2ecc71">ongoing</strong>' : toLocalDate(d.endDate)?.toLocaleDateString()}<br/>
           Updated by: ${d.updatedBy}`
        )
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', () => {
        tooltip.transition()
          .duration(500)
          .style('opacity', 0);
      });
  }, [isOpen, projectHistory]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title>
            <span>{employeeName}</span>
          </Title>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <ChartContainer ref={chartRef} />
      </ModalContent>
    </ModalOverlay>
  );
};

export default ProjectHistoryModal; 